import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { orgDisplayName, type OrgNameSource } from '@/lib/groups/groupKind';

// Organizations read once per page session, so the groups list doesn't
// re-read organizations/{id} on every render (mobile useOrgNames does the same).
const resolvedOrgs = new Map<string, OrgNameSource | null>();

/**
 * Display names for organization ids, Arabic name in RTL (orgDisplayName).
 * Mirrors namat-mobile src/hooks/useOrgNames.ts.
 */
export function useOrgNames(orgIds: ReadonlyArray<string>, isRTL: boolean): Record<string, string> {
  const [version, setVersion] = useState(0);
  const idsKey = useMemo(() => Array.from(new Set(orgIds.filter(Boolean))).sort().join(','), [orgIds]);

  useEffect(() => {
    if (!idsKey) return;
    const missing = idsKey.split(',').filter((id) => !resolvedOrgs.has(id));
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(
      missing.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, 'organizations', id));
          resolvedOrgs.set(id, snap.exists() ? ({ id, ...snap.data() } as OrgNameSource) : null);
        } catch {
          // Left unresolved: the badge shows its icon without a name.
        }
      }),
    ).then(() => {
      if (!cancelled) setVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return useMemo(() => {
    const out: Record<string, string> = {};
    if (!idsKey) return out;
    for (const id of idsKey.split(',')) {
      const name = orgDisplayName(resolvedOrgs.get(id) ?? undefined, isRTL);
      if (name) out[id] = name;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, isRTL, version]);
}
